import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The one external-link primitive (design-grammar.md §4.6).
 *
 * "External links go through one `ExternalLink` primitive: `target="_blank"`,
 * `rel="noopener noreferrer"`, and an `sr-only` 'opens in a new tab'."
 *
 * Two defects it closes at once. `target="_blank"` without `rel="noopener"`
 * hands the opened page a live `window.opener` reference back into the app —
 * about half the 19 sites measured 2026-08-26 were missing it. And a link
 * that silently replaces the reader's context is a trap for anyone who cannot
 * SEE the new tab appear; the sr-only suffix is the only announcement they get.
 *
 * NAME COLLISION: `lucide-react` also exports an `ExternalLink` (the arrow
 * glyph). A file using both imports the icon aliased —
 * `import { ExternalLink as ExternalLinkIcon } from 'lucide-react'` — because
 * the grammar names the primitive, and the primitive is the one that has to
 * read as itself at the call site.
 */

/**
 * The announcement. One string, so a reader hears the same words everywhere.
 * The English value of `common:links.opensInNewTab` (#2109), which is what
 * renders; kept as a constant for the tests that assert the English words.
 */
export const NEW_TAB_LABEL = 'opens in a new tab';

export interface ExternalLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel'> {
  href: string;
  children: ReactNode;
}

export const ExternalLink = forwardRef<HTMLAnchorElement, ExternalLinkProps>(
  function ExternalLink({ href, children, ...rest }, ref) {
    const { t } = useTranslation('common');
    return (
      <a ref={ref} href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
        {/* Leading space so a reader hears "Donate, opens in a new tab" rather
            than the two run together. Visually hidden, so no layout effect. */}
        <span className="sr-only"> ({t('links.opensInNewTab')})</span>
      </a>
    );
  },
);
